/**
 * LocationChangePrompt/index.js
 * desc: component will block navigation and show a confirmation modal if user has unsaved changes
 * note: wrapper around Prompt component
 */
'use strict';

import { useCallback, useRef, useEffect } from "react";
import { showUnsavedChangesConfirmation } from '../../utilities';
import appUrls from '../../settings/app-urls';
import {
    useBeforeUnload,
    useBlocker,
    useMatch,
} from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { selectHasUnsavedChanges } from "../../store/selectors/app";
import { setHasUnsavedChanges } from "../../store/actions/app";
 
function usePrompt() {
    const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
    const dispatch = useDispatch();

    // check if the current page is Review Details page
    const match = useMatch(appUrls.REVIEWS_DETAIL);

    let blocker = useBlocker(
        useCallback(
            (params) => {
                // we need to fully reload the page each time we navigate from Review Detail page
                // because we need to get new headers from the server since CSP headers for Review Detail page
                // are different from the rest of the apps pages.
                if (match) {
                    window.location.href = params.nextLocation.pathname;
                    return true;
                }
                return hasUnsavedChanges;
            },
            [ hasUnsavedChanges ]
        )
    );

    // guards against showing a second confirmation while one is already open — useBlocker can
    // hand back a new `blocker` object reference while `state` stays 'blocked', which would
    // otherwise re-run the effect and stack duplicate prompts
    const isPromptOpenRef = useRef(false);

    // react-router throws an "Invalid blocker state transition" invariant if proceed()/reset()
    // runs once the blocker has left the 'blocked' state (only blocked -> proceeding/unblocked
    // is allowed). useBlocker hands back a per-render snapshot, so the captured `blocker.state`
    // can still read 'blocked' while the live router blocker has already resolved — e.g. the
    // redux unsaved-changes flag and the router blocker update land on separate commits, or the
    // confirmation modal outlives the navigation it guarded. In every such case the navigation
    // is already settled, so the call is a safe no-op and the invariant is benign here.
    const runBlockerAction = (action) => {
        try {
            action();
        } catch (err) {
            // Only tolerate the benign 'already advanced past blocked' invariant;
            // surface anything else so real failures stay observable.
            if (!/blocker/i.test(err?.message ?? '')) {
                throw err;
            }
        }
    };

    // recover from a stale block: a navigation can get blocked while the flag is still `true`
    // and then have the flag cleared in the same tick — e.g. a save that dispatches
    // setHasUnsavedChanges(false) and immediately navigates to the new record's page. The
    // blocker callback saw the pre-clear `true`, so the navigation is left stuck 'blocked' with
    // no prompt; the next change to set the flag would then surface a spurious confirmation.
    // With nothing unsaved there is nothing to guard, so let it through. (The Review Detail
    // `match` case blocks intentionally to force a full reload, so it's excluded.)
    // Skip while a prompt is open: proceeding out from under it would leave the stale modal's
    // OK/Cancel to call proceed()/reset() on an already-unblocked blocker, which throws.
    useEffect(() => {
        if (blocker.state === 'blocked' && !hasUnsavedChanges && !match && !isPromptOpenRef.current) {
            runBlockerAction(() => blocker.proceed());
        }
    }, [ blocker, hasUnsavedChanges, match ]);

    // run after commit (not during render) — showing the confirmation dispatches a toastr
    // action, and doing that mid-render warns about updating another component while rendering
    useEffect(() => {
        if (blocker.state === 'blocked' && hasUnsavedChanges && !isPromptOpenRef.current) {
            isPromptOpenRef.current = true;
            showUnsavedChangesConfirmation({
                onOk: () => {
                    isPromptOpenRef.current = false;
                    dispatch(setHasUnsavedChanges(false));
                    runBlockerAction(() => blocker.proceed());
                },
                onCancel: () => {
                    isPromptOpenRef.current = false;
                    runBlockerAction(() => blocker.reset());
                }
            });
        }
    }, [ blocker, hasUnsavedChanges, dispatch ]);

    useBeforeUnload(
        useCallback(
            (event) => {
                if (hasUnsavedChanges) {
                    event.preventDefault();
                    event.returnValue = true;
                }
            },
            [ hasUnsavedChanges ]
        ),
        { capture: true }
    );
}
 
 
const LocationChangePrompt = () => {

    usePrompt();
 
    return <></>;
};
 
export default LocationChangePrompt;
